import React, { useState, useEffect } from 'react';
import apiClient from '../api/api';
import { 
    FaUser, FaBuilding, FaBirthdayCake, FaVenusMars, FaIdCard, 
    FaMapMarkerAlt, FaTimes, FaCamera, FaSave, FaLeaf 
} from 'react-icons/fa';
import { Map, MapMarker, MapControls } from './ui/map';
import loadImage from '../assets/imageLoader';

const EditBuyerProfileModal = ({ showModal, onClose, buyerData, onSave }) => {
  // --- States ---
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    date_of_birth: '',
    gender: '',
    nid_or_passport: '',
    division: '',
    district: '',
    upazila: '',
    granular_location: ''
  });
  
  const [picture, setPicture] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [idDocument, setIdDocument] = useState(null);
  const [companyDoc, setCompanyDoc] = useState(null);
  const [mapPosition, setMapPosition] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showMap, setShowMap] = useState(false);

  // --- Populate Form ---
  useEffect(() => {
    if (buyerData) {
      const profile = buyerData.profile || buyerData;
      const buyerDetails = buyerData.buyer_details || buyerData.buyer || {};

      setFormData({
        name: profile.name || '',
        company_name: buyerDetails.organisation || '',
        date_of_birth: profile.date_of_birth ? new Date(profile.date_of_birth).toISOString().split('T')[0] : '',
        gender: profile.gender || '',
        nid_or_passport: profile.nid_or_passport || '',
        division: profile.location?.division || '',
        district: profile.location?.district || '',
        upazila: profile.location?.upazila || '',
        granular_location: profile.location?.granular_location || ''
      });

      if (profile.location?.lat && profile.location?.lng) {
        setMapPosition({ lat: profile.location.lat, lng: profile.location.lng });
      } else {
        // Default to Dhaka if no location set
        setMapPosition({ lat: 23.8103, lng: 90.4125 });
      }
      
      setPreviewImage(loadImage(profile.picture));
    }
  }, [buyerData]);

  // --- Handlers ---
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setPicture(e.target.files[0]);
      setPreviewImage(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleMapClick = async (coords) => {
    setMapPosition(coords);
    try {
        const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}`);
        const data = await response.json();
        if (data && data.address) {
            setFormData(prev => ({
                ...prev,
                division: data.address.state || data.address.region || prev.division,
                district: data.address.city || data.address.county || prev.district,
                upazila: data.address.suburb || data.address.town || prev.upazila,
                granular_location: data.display_name
            }));
        }
    } catch (err) {
        console.error("Reverse geocoding failed", err);
    }
  };

  const handleGeolocate = (coords) => {
    setMapPosition(coords);
    handleMapClick(coords);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.name || !formData.gender || !formData.nid_or_passport) {
      setError('Please fill in required fields (Name, Gender, NID).');
      return;
    }

    const payload = new FormData();
    Object.keys(formData).forEach(key => payload.append(key, formData[key]));
    if (picture) payload.append('picture', picture);
    if (idDocument) payload.append('id_document', idDocument);
    if (companyDoc) payload.append('company_document', companyDoc);
    
    // Add coordinates
    if (mapPosition) {
        payload.append('lat', mapPosition.lat);
        payload.append('lng', mapPosition.lng);
    }

    setIsSaving(true);
    setError('');

    try {
      await apiClient.put('/buyer/profile', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (onSave) onSave();
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to save profile.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm transition-all">
      <div className="bg-white w-full max-w-5xl h-[90vh] rounded-[2rem] shadow-2xl flex flex-col overflow-hidden relative animate-fade-in-up">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-white z-10">
            <div>
                <h2 className="text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
                    <FaUser className="text-emerald-500" /> Edit Profile
                </h2>
                <p className="text-slate-500 text-sm font-medium">Update your personal details and location.</p>
            </div>
            <button onClick={onClose} className="p-3 bg-slate-100 rounded-full text-slate-500 hover:bg-red-50 hover:text-red-500 transition-colors">
                <FaTimes size={18} />
            </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar bg-slate-50">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 h-full">
                
                {/* Left: Form */}
                <div className="lg:col-span-2 p-8 space-y-6">
                    
                    {/* Profile Pic */}
                    <div className="flex justify-center mb-6">
                        <div className="relative group">
                            <img 
                                src={previewImage} 
                                alt="Profile" 
                                className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg group-hover:opacity-90 transition-opacity"
                            />
                            <label className="absolute bottom-0 right-0 bg-emerald-600 text-white p-2.5 rounded-full cursor-pointer hover:bg-emerald-700 shadow-md transition-transform hover:scale-110">
                                <FaCamera size={14} />
                                <input type="file" className="hidden" onChange={handleFileChange} accept="image/*" />
                            </label>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Full Name *</label>
                            <div className="relative">
                                <FaUser className="absolute left-3 top-3.5 text-slate-300" />
                                <input 
                                    name="name" 
                                    value={formData.name} 
                                    onChange={handleChange} 
                                    className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all font-bold text-slate-700" 
                                    placeholder="Your Name"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Organization</label>
                            <div className="relative">
                                <FaBuilding className="absolute left-3 top-3.5 text-slate-300" />
                                <input 
                                    name="company_name" 
                                    value={formData.company_name} 
                                    onChange={handleChange} 
                                    className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all font-bold text-slate-700" 
                                    placeholder="Company Name"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Date of Birth</label>
                                <div className="relative">
                                    <FaBirthdayCake className="absolute left-3 top-3.5 text-slate-300" />
                                    <input 
                                        type="date"
                                        name="date_of_birth" 
                                        value={formData.date_of_birth} 
                                        onChange={handleChange} 
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all font-bold text-slate-700" 
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Gender *</label>
                                <div className="relative">
                                    <FaVenusMars className="absolute left-3 top-3.5 text-slate-300" />
                                    <select 
                                        name="gender" 
                                        value={formData.gender} 
                                        onChange={handleChange} 
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all font-bold text-slate-700 appearance-none"
                                    >
                                        <option value="">Select</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">NID / Passport *</label>
                            <div className="relative">
                                <FaIdCard className="absolute left-3 top-3.5 text-slate-300" />
                                <input 
                                    name="nid_or_passport" 
                                    value={formData.nid_or_passport} 
                                    onChange={handleChange} 
                                    className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all font-bold text-slate-700" 
                                    placeholder="ID Number"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 pt-4 border-t border-slate-100">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Document Uploads</p>
                            
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-slate-500 ml-1">ID Card (NID/Passport)</label>
                                <input 
                                    type="file" 
                                    onChange={e => setIdDocument(e.target.files[0])}
                                    className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-[10px] file:font-black file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 transition-all"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-slate-500 ml-1">Company Trade License</label>
                                <input 
                                    type="file" 
                                    onChange={e => setCompanyDoc(e.target.files[0])}
                                    className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-[10px] file:font-black file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-all"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right: Map & Location */}
                <div className="lg:col-span-3 bg-white relative flex flex-col border-l border-slate-200">
                    <div className="absolute top-4 left-4 right-4 z-10 bg-white/90 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-emerald-100">
                        <div className="flex items-start gap-3">
                            <div className="mt-1 text-emerald-600"><FaMapMarkerAlt size={20} /></div>
                            <div className="flex-1">
                                <p className="text-[10px] font-black uppercase text-emerald-600 tracking-wider mb-1">Selected Location</p>
                                <p className="text-sm font-bold text-slate-800 leading-snug">
                                    {formData.granular_location || "Tap the map to set your location"}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex-grow relative h-[400px] lg:h-auto bg-slate-100">
                         <Map 
                            center={mapPosition ? [mapPosition.lng, mapPosition.lat] : [90.4125, 23.8103]} 
                            zoom={13} 
                            onClick={handleMapClick}
                            className="h-full w-full" 
                        >
                            <MapControls showLocate onGeolocate={handleGeolocate} showZoom />
                            <MapMarker 
                                longitude={mapPosition?.lng || 90.4125} 
                                latitude={mapPosition?.lat || 23.8103} 
                                draggable 
                                onDragEnd={handleMapClick} 
                            />
                        </Map>
                    </div>
                    
                    <div className="p-4 bg-slate-50 text-center text-xs text-slate-400 font-bold uppercase tracking-wider border-t border-slate-100">
                        Use the GPS button or drag the pin to set your precise address.
                    </div>
                </div>

            </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-white border-t border-slate-100 flex justify-between items-center z-20">
            {error && <p className="text-red-500 text-sm font-bold bg-red-50 px-3 py-1 rounded-lg">{error}</p>}
            <div className="flex gap-3 ml-auto">
                <button onClick={onClose} className="px-6 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-colors" disabled={isSaving}>
                    Cancel
                </button>
                <button 
                    onClick={handleSave} 
                    className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                    disabled={isSaving}
                >
                    {isSaving ? 'Saving...' : <><FaSave /> Save Changes</>}
                </button>
            </div>
        </div>

      </div>
    </div>
  );
};

export default EditBuyerProfileModal;